import Vue from "vue";
import Router from "vue-router";
import modules from "./modules";
import { flatten } from "lodash-es";
import store from "@/store";
import { SET_INFO } from "@/store/info/actions";
import { ACTION_RESET_REGO_SEARCHED, ACTION_RESET_REGO_LOOKUP_COMPLETED } from "@/store/rego-search/actions";
import { GET_NZ_VARIABLES } from "@/store/common-variables/actions";
import { VUE_APP_BASE } from "@/config";
import { LP_TYPE } from "@/config";
import topPageService from "@/services/top-page";
import {
  ACTION_REGO_SEARCH,
  ACTION_REGO_SEARCHED,
  ACTION_REGO_LOOKUP_COMPLETED,
} from "@/store/rego-search/actions";
import { SET_REGO_SEARCH_FLAG } from "@/store/info/actions";
import { SET_STATE } from "../store/info/actions";

import {
  CacheStatus,
  CACHE_MODULE_NAME,
  CACHE_STATUS,
  CLEAR_STORE_CACHE,
} from "../store/plugins/session-storage-persistence";
import { eraseCookie, getCookie, setCookie } from "../helper/cookie";
import { isEmpty, decrypt, b64dec, logError } from "@/helper/utils";

Vue.use(Router);
const router = new Router({
  base: VUE_APP_BASE,
  mode: "history",
  routes: [
    {
      path: "/",
      name: "top-page",
      component: () => import("../views/top-page/Index"),
    },
    {
      path: "/",
      name: "layout",
      component: () => import("../components/layout/Index"),
      children: [...flatten(Object.values(modules))],
    },
    {
      path: "/*",
      name: "not-found",
      component: () => import("../views/error/404"),
    },
  ],
  scrollBehavior: scrollTop,
});

const route = {
  TOP_PAGE: "top-page",
  THANK_PAGE: "thanks-page",
};

function scrollTop(to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition;
  } else {
    return { x: 0, y: 0 };
  }
}

function resetStore(pathName) {
  if (pathName === route.TOP_PAGE) {
    store.dispatch("resetStep");
    store.dispatch("clearImages");
    store.dispatch("clearImagesWithPersistence");
    store.dispatch(SET_INFO, {});
    store.dispatch(ACTION_RESET_REGO_SEARCHED);
    store.dispatch(SET_REGO_SEARCH_FLAG, false);
    store.dispatch(ACTION_RESET_REGO_LOOKUP_COMPLETED);
  }
  store.commit(CLEAR_STORE_CACHE);
}

function setNextStep(nextStep) {
  store.dispatch("setStep", nextStep);
}

function setCacheStatus(status) {
  store.state[CACHE_MODULE_NAME][CACHE_STATUS] = status;
}

function decryptBpid() {
  // console.log(getCookie("qs"));
  const queryString = getCookie("qs");
  // console.log('queryString',queryString)
  if (queryString) {
    try {
      store.dispatch("setQueryString", queryString);
      const newURL = new URLSearchParams(queryString);
      const encParam = newURL.get('enc');
      if (encParam) {
        let buf = decodeURIComponent(encParam).replace(/ /g, "+");
        const parts = buf.split(':');
        const encryptedString = Buffer.from(parts[1], 'base64');
        const decrypted = decrypt(b64dec(encryptedString));
        console.log('decrypted',decrypted);
        const urlParams = new URLSearchParams(decrypted);

        return urlParams;
      }
    } catch (err) {
      logError(`[Router] decryptBpid error: ${err}`);
    }
  }
}
  
let lookupValue = "";
let regoValue = "";

async function regoSearch(to, from, next) {
  if (store.getters.searched === false ) {
    store.dispatch(ACTION_REGO_SEARCHED);
    store.dispatch('getSystemConfigurations');
    try {
      const searchResult = await topPageService.searchRego(
        {
          lookup: lookupValue,
          regoNumber: regoValue,
          leadSource: LP_TYPE,
          locid: 2,
        }
      );
      if (searchResult.status === 201) {
        searchResult.data.rego = regoValue.toUpperCase();
        store.dispatch(SET_REGO_SEARCH_FLAG, true);
        store.dispatch(ACTION_REGO_LOOKUP_COMPLETED);
        store
          .dispatch(ACTION_REGO_SEARCH, searchResult.data)
          .then(() => {
            next({ name: "vehicle-info", query: to.query });
          });
      }
    } catch (err) {
      if (err.response.status === 404) {
        Vue.$toast.warning(
          "We were unable to find your car information via our Rego search. Please fill in the information below."
        );
        store
          .dispatch(ACTION_REGO_SEARCH, {
            rego: regoValue.toUpperCase(),
          })
          .then(() => {
            next({ name: "vehicle-info", query: to.query });
          });
      } else {
        logError(`[Router] Rego search error: ${err}`);
      }
    }
  }
}

router.beforeEach((to, from, next) => {
  if (!isEmpty(to.query)) {
    if (getCookie("qs")) eraseCookie("qs");
    setCookie("qs", new URLSearchParams(to.query).toString(), 1);
    window.localStorage.setItem('qs', JSON.stringify(Object.assign({}, to.query)));
  } else {
    eraseCookie("qs");
  }

  const excludeRouteNames = [
    "privacy-policy",
    "terms-and-conditions",
    "photo-guide",
    "seller-fees",
    "seller-agreement",
  ];

  const excludeToPath = [
    "top-page",
    "privacy-policy",
    "terms-and-conditions",
    "photo-guide",
    "seller-fees",
    "seller-agreement",
  ];

  if (excludeRouteNames.includes(to.name)) {
    next();
    return;
  }

  let urlParams = "";

  if (lookupValue === "" || lookupValue === null) {
    urlParams = decryptBpid();

    if (urlParams) {
      !urlParams.get("lookup") ? lookupValue = '0' : lookupValue = urlParams.get("lookup");
      regoValue = urlParams.get("rego");
    }
  }

  switch (store.state[CACHE_MODULE_NAME][CACHE_STATUS]) {
    case CacheStatus.New:
      // console.log(CacheStatus.New, Date.now());
      if (lookupValue !== '0') {
        resetStore(to.name);
      }
      setCacheStatus(CacheStatus.Rehydrated);
      if (!store?.getters.step || store.getters.step === 0) {
        if (urlParams !== undefined && regoValue === '') {
          store.dispatch(GET_NZ_VARIABLES).then(() => {
            store.dispatch('getSystemConfigurations');
            next({ name: "vehicle-info", query: to.query });
          })
        } else if (lookupValue === "1") {
          store.dispatch(GET_NZ_VARIABLES).then(() => {
            regoSearch(to, from, next);
          })
        } else {
          next({ name: "top-page", query: to.query });
        }
      } else if (lookupValue === "0") {
        store.dispatch(GET_NZ_VARIABLES).then(() => {
          store.dispatch('getSystemConfigurations');
          next({ name: "vehicle-info", query: to.query });
          store.dispatch(SET_STATE, {
            regoValue: regoValue.toUpperCase(),
          });
        })
      } else {
        next({
          name: store.getters.currentStep.name,
          query: from.query,
        });
      }
      break;

    case CacheStatus.Empty:
      // console.log(CacheStatus.Empty, Date.now());
      setCacheStatus(CacheStatus.New);
      if (urlParams) {
        if (lookupValue === "0") {
          setNextStep(1);
            store.dispatch("setProgress", 0);
            next({
              name: "vehicle-info",
              query: to.query,
            });
        } else {
          next({
            name: "vehicle-info",
            query: to.query,
          });
        }
      } else {
        next({ name: "top-page", query: to.query });
      }
      break;

    default:
      if (to.name === route.TOP_PAGE || to.name === route.THANK_PAGE) {
        resetStore(to.name);
        next();
      } else if (
        from.name === "thanks-page" &&
        !excludeToPath.includes(to.name)
      ) {
        next({ name: "top-page" });
      } else {
        // check step
        if (to.meta.nextable) {
          next();
        } else {
          setNextStep(to.meta.step);
          if (from.name === "top-page" && to.name === "vehicle-info") {
            store.dispatch("setProgress", 0);
            window.localStorage.setItem('qs', JSON.stringify(Object.assign({}, to.query)));
            store.dispatch("resetProgressStep");
          }
          next();
        }
      }
      break;
  }
});

router.afterEach((to, from) => {
  const refreshAblePaths = ['vehicle-info', 'photo-upload', 'customer-detail'];
  if (from.name === null && refreshAblePaths.includes(to.name) && lookupValue === "") {
    // get query string from localStorage
    const queryObject = JSON.parse(window.localStorage.getItem('qs'));
    if (!isEmpty(queryObject)) {
      const mergedQuery = Object.assign({}, to.query, queryObject);
      router.replace({ name: to.name, query: mergedQuery });
    }
  }
})

export default router;
